import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../lib/supabase';
import { registerModalBackHandler } from '../lib/native';
import { Spinner } from './design-system';

interface FeedbackPopoverProps {
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
}

export function FeedbackPopover({ anchorRef, onClose }: FeedbackPopoverProps) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [style, setStyle] = useState<React.CSSProperties | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const maxLen = 2000;

  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      setStyle({ bottom: 16, left: 16, right: 16, width: 'auto' });
    } else {
      setStyle({ bottom: window.innerHeight - rect.bottom, left: rect.right + 12 });
    }
  }, [anchorRef]);

  // Android hardware back button closes this popover instead of navigating
  // the route underneath it.
  useEffect(() => registerModalBackHandler(onClose), [onClose]);

  async function handleSend() {
    if (!message.trim() || sending) return;
    setSending(true);
    try {
      await supabase.functions.invoke('send-feedback', {
        body: { message: message.trim() },
      });
    } catch { /* silent */ }
    setSent(true);
    setTimeout(onClose, 1500);
  }

  if (!style) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[60]" onClick={onClose} />

      {/* Popover anchored above the Feedback button */}
      <div
        ref={popoverRef}
        onClick={(e) => e.stopPropagation()}
        style={style}
        className="fixed w-72 md:w-72 bg-app-card border border-app-border rounded-2xl shadow-2xl p-4 flex flex-col gap-3 z-[61]"
      >
        {sent ? (
          <div className="flex items-center gap-2 py-3 justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-success">
              <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
            </svg>
            <span className="text-sm text-app-fg font-medium">Thanks for your feedback!</span>
          </div>
        ) : (
          <>
            <div>
              <h4 className="text-sm font-semibold text-app-fg">Share your feedback</h4>
              <p className="text-xs text-app-muted mt-0.5">Ideas to improve this page or any general feedback</p>
            </div>

            <div className="relative">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, maxLen))}
                rows={3}
                maxLength={maxLen}
                autoFocus
                placeholder="What's on your mind?"
                className="w-full rounded-xl border border-app-border bg-app-bg px-3 py-2.5 text-sm text-app-fg placeholder:text-app-muted focus:border-gold focus:outline-none resize-none"
              />
              <span className="absolute bottom-2 right-3 text-[10px] text-app-muted">{message.length}/{maxLen}</span>
            </div>

            <button
              onClick={() => void handleSend()}
              disabled={!message.trim() || sending}
              className="self-start inline-flex items-center gap-2 bg-violet text-cream font-semibold text-sm px-4 py-2 rounded-full hover:brightness-105 transition-all disabled:opacity-40"
            >
              {sending ? (
                <Spinner size="sm" className="text-cream" />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path d="M3.105 2.289a.75.75 0 00-.826.95l1.414 4.925A1.5 1.5 0 005.135 9.25h6.115a.75.75 0 010 1.5H5.135a1.5 1.5 0 00-1.442 1.086l-1.414 4.926a.75.75 0 00.826.95 28.896 28.896 0 0015.293-7.154.75.75 0 000-1.115A28.897 28.897 0 003.105 2.289z" />
                </svg>
              )}
              Send
            </button>
          </>
        )}
      </div>
    </>,
    document.body,
  );
}
